import { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Combobox, ETypeOfNotification, GetItemsProps, InputColumnTableProps, InputSwitch, InputTable, InputTextCustom, Modal, setNotification } from "medere-ui-framework";
import { useDispatch } from "react-redux";
import useResizeListener from "../../../../../hooks/useResizeListener";
import { defaultRoom, Room, RoomType, Sector } from "../../../../../interface/interface";
import { saveRoom } from "../../../../../servicesHooks/Location/useLocationConfigService";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";
import LocationInlineEditActions from "../../../components/LocationInlineEditActions";
import { isRoomBlockedSectorType } from "../../../roomSectorRules";

interface RoomTableProps {
    rooms: Room[];
    sectors: Sector[];
    roomTypes: RoomType[];
    estId: number;
    onDelete: (room: Room) => void;
    onCreateBed: (room: Room) => void;
    onRefresh: () => Promise<void> | void;
    mobileTitle?: string;
    mobileSubtitle?: string;
}

export default function RoomTable({
    rooms,
    sectors,
    roomTypes,
    estId,
    onDelete,
    onCreateBed,
    onRefresh,
    mobileTitle,
    mobileSubtitle
}: Readonly<RoomTableProps>) {
    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);

    const [roomItem, setRoomItem] = useState<Room>({ ...defaultRoom, building: estId });
    const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
    const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null);
    const [roomErrors, setRoomErrors] = useState<{ number?: string; sector?: string }>({});
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [mobileModalVisible, setMobileModalVisible] = useState(false);
    const isEditing = editIndex !== null;
    const allowedSectors = useMemo(() => (sectors ?? []).filter((sector) => !isRoomBlockedSectorType(sector.sectortype)), [sectors]);
    const blockedSectorIds = useMemo(() => new Set((sectors ?? []).filter((sector) => isRoomBlockedSectorType(sector.sectortype)).map((sector) => sector.sector)), [sectors]);
    const canCreateRooms = allowedSectors.length > 0;

    const isBlockedRoom = (room: Room) => blockedSectorIds.has(room.sector);

    const getRoomTypeItems = ({ filterValue }: GetItemsProps) => {
        const filter = (filterValue ?? "").trim().toLowerCase();
        return Promise.resolve(filter ? roomTypes.filter((rt) => rt.description.toLowerCase().includes(filter)) : roomTypes);
    };

    const handleRoomTypeChange = (value: RoomType | null) => {
        setSelectedRoomType(value);
        setRoomItem((current) => ({
            ...current,
            roomtype: value?.roomtype ?? null,
            roomtypedescription: value?.description ?? null
        }));
    };

    const validateRoom = (room: Room) => {
        const errors: { number?: string; sector?: string } = {};
        if (!room.number?.trim()) errors.number = "El nombre es requerido";
        if (!room.sector || room.sector <= 0) errors.sector = "Debe seleccionar un sector";
        return errors;
    };

    const resetEditor = () => {
        setRoomItem({ ...defaultRoom, building: estId });
        setSelectedSector(null);
        setSelectedRoomType(null);
        setRoomErrors({});
        setEditIndex(null);
    };

    const executeSave = async () => {
        const errors = validateRoom(roomItem);
        setRoomErrors(errors);
        if (Object.keys(errors).length > 0) return false;

        try {
            await saveRoom({
                ...roomItem,
                room: roomItem.room > 0 ? roomItem.room : -1,
                bedsquantity: roomItem.bedsquantity ?? 0
            });
            dispatch(setNotification({
                title: roomItem.room > 0 ? "Edición exitosa" : "Creación exitosa",
                desc: roomItem.room > 0 ? "Se actualizó la habitación." : "Se creó la habitación.",
                icon: "pi pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO
            }));
            await onRefresh();
            resetEditor();
            return true;
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 409) {
                dispatch(setNotification({ title: "No se pudo guardar", desc: "Ya existe una habitación con ese nombre en el sector.", icon: "pi pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
            } else {
                dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo guardar la habitación.", icon: "pi pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
            }
            return false;
        }
    };

    const handleAdd = () => {
        if (!canCreateRooms) return;
        void executeSave();
    };

    const handleEdit = (item: Room, index: number) => {
        if (isBlockedRoom(item)) return;
        setRoomItem(item);
        const sector = sectors.find((sec) => sec.sector === item.sector) ?? null;
        setSelectedSector(sector);
        setSelectedRoomType(roomTypes.find((rt) => rt.roomtype === item.roomtype) ?? null);
        setEditIndex(index);
    };

    const toggleActive = async (item: Room) => {
        if (isBlockedRoom(item)) return;
        try {
            await saveRoom({ ...item, active: !item.active });
            await onRefresh();
        } catch (error) {
            dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo cambiar el estado de la habitación.", icon: "pi pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
        }
    };

    const openAddModal = () => {
        if (!canCreateRooms) return;
        resetEditor();
        setMobileModalVisible(true);
    };

    const openEditModal = (item: Room, index: number) => {
        if (isBlockedRoom(item)) return;
        handleEdit(item, index);
        setMobileModalVisible(true);
    };

    const columns: InputColumnTableProps<Room>[] = [
        {
            header: "Nombre",
            className: "location-inputtable-fluid-col location-col-room-name",
            body: (item) => item.number,
            input: (
                <InputTextCustom
                    className={(roomErrors.number ? "" : "border-grey") + " w-full"}
                    value={roomItem.number ?? ""}
                    onChange={(e) => {
                        setRoomItem({ ...roomItem, number: e.target.value });
                        setRoomErrors((prev) => ({ ...prev, number: "" }));
                    }}
                    error={!!roomErrors.number}
                    caption={roomErrors.number}
                />
            ),
        },
        {
            header: "Sector",
            className: "location-inputtable-fluid-col location-col-room-sector",
            body: (item) => item.sectorname,
            input: (
                <Combobox
                    className={(roomErrors.sector ? "" : "border-grey") + " w-full"}
                    getItems={() => Promise.resolve(allowedSectors)}
                    optionLabel="name"
                    value={selectedSector}
                    setValue={(value: Sector | null) => {
                        setSelectedSector(value);
                        setRoomItem({
                            ...roomItem,
                            sector: value?.sector ?? -1,
                            sectorname: value?.name ?? ""
                        });
                        setRoomErrors((prev) => ({ ...prev, sector: "" }));
                    }}
                    error={!!roomErrors.sector}
                    caption={roomErrors.sector}
                />
            ),
        },
        {
            header: "Tipo",
            className: "location-inputtable-fluid-col location-col-room-type",
            body: (item) => item.roomtypedescription || "—",
            input: (
                <Combobox
                    className="border-grey w-full"
                    getItems={getRoomTypeItems}
                    reLoadItemsValue={roomTypes}
                    optionLabel="description"
                    placeholder="Sin tipo"
                    autoselectOnOneItem={false}
                    value={selectedRoomType}
                    setValue={handleRoomTypeChange}
                    noItemsCaption="Creá los tipos desde «Tipos de habitación»"
                />
            ),
        },
        {
            header: "Q. Camas",
            className: "location-inputtable-fluid-col location-col-room-beds",
            body: (item) => item.bedsquantity ?? 0,
        },
        {
            header: "Estado",
            className: "location-inputtable-fluid-col location-col-room-state",
            body: (item) => (item.active ? "Activo" : "Inactivo"),
            input: (
                <div className="flex align-items-center gap-2 mt-2 location-switch-inline-row location-desktop-inputtable-switch-row">
                    <InputSwitch
                        id="inputswitchactiveroomid"
                        checked={roomItem.active}
                        onChange={(e) => setRoomItem({ ...roomItem, active: e.value })}
                    />
                    <span className="location-switch-state-label">{roomItem.active ? "Activo" : "Inactivo"}</span>
                </div>
            ),
        },
        {
            header: "",
            className: "location-inputtable-control-column",
            input: (
                isEditing ? (
                    <LocationInlineEditActions
                        visible
                        onConfirm={handleAdd}
                        onCancel={resetEditor}
                    />
                ) : (
                    <Button
                        type="button"
                        icon="pi pi-plus-circle text-3xl"
                        className="buttonSecondary2 p-button-rounded location-inputtable-add-inline-btn"
                        disabled={!canCreateRooms}
                        onClick={handleAdd}
                        aria-label="Crear"
                    />
                )
            ),
            body: (item, index) => {
                const blockedRoom = isBlockedRoom(item);
                const menuItems: LocationActionItem[] = [
                    {
                        label: "Editar",
                        icon: "pi pi-file-edit",
                        disabled: blockedRoom,
                        action: () => handleEdit(item, index),
                    },
                    {
                        label: "Crear cama",
                        icon: "pi pi-plus",
                        disabled: blockedRoom,
                        action: () => onCreateBed(item),
                    },
                    {
                        label: item.active ? "Desactivar" : "Activar",
                        icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                        disabled: blockedRoom,
                        action: () => { void toggleActive(item); },
                    },
                    {
                        label: "Eliminar",
                        icon: "pi pi-trash",
                        disabled: blockedRoom,
                        action: () => onDelete(item),
                    },
                ];

                return (
                    <div className="flex justify-content-end">
                        <LocationActionMenu
                            items={menuItems}
                            ariaLabel="Opciones de la habitación"
                            buttonClassName="p-button-text location-inputtable-menu-btn"
                        />
                    </div>
                );
            }
        }
    ];

    if (isMobile) {
        return (
            <div className="location-config-view">
                <div className="location-mobile-block-header">
                    <div>
                        <h4 className="location-mobile-card-title">{mobileTitle ?? "Establecimiento"}</h4>
                        {!!mobileSubtitle && <p className="grey-color text-sm mt-1 mb-0">{mobileSubtitle}</p>}
                    </div>
                    <Button className="location-mobile-add-btn" icon="pi pi-plus-circle" label="Agregar" onClick={openAddModal} disabled={!canCreateRooms} />
                </div>

                <div className="location-mobile-card-list">
                    {(rooms ?? []).map((item, index) => {
                        const blockedRoom = isBlockedRoom(item);
                        const menuItems: LocationMobileActionItem[] = [
                            { label: "Editar", icon: "pi pi-file-edit", disabled: blockedRoom, action: () => openEditModal(item, index) },
                            { label: "Crear cama", icon: "pi pi-plus", disabled: blockedRoom, action: () => onCreateBed(item) },
                            { label: item.active ? "Desactivar" : "Activar", icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle", disabled: blockedRoom, action: () => { void toggleActive(item); } },
                            { label: "Eliminar", icon: "pi pi-trash", disabled: blockedRoom, action: () => onDelete(item) },
                        ];

                        return (
                            <article key={`location-room-${item.room}-${index}`} className="location-mobile-card">
                                <div className="location-mobile-card-header">
                                    <h4 className="location-mobile-card-title">{item.number || "Sin nombre"}</h4>
                                    <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones de la habitación" />
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Sector</span>
                                    <span className="location-mobile-card-value">{item.sectorname || "-"}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Tipo</span>
                                    <span className="location-mobile-card-value">{item.roomtypedescription || "—"}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Q. Camas</span>
                                    <span className="location-mobile-card-value">{item.bedsquantity ?? 0}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Estado</span>
                                    <span className="location-mobile-card-value">{item.active ? "Activo" : "Inactivo"}</span>
                                </div>
                            </article>
                        );
                    })}
                    {(rooms ?? []).length === 0 && (
                        <p className="location-mobile-empty">No hay habitaciones cargadas.</p>
                    )}
                </div>

                <Modal
                    className="location-mobile-editor-modal"
                    visible={mobileModalVisible}
                    setVisible={setMobileModalVisible}
                    width="w-medium"
                    header={<h1 className="info-dark text-2xl">{roomItem.room > 0 ? "Editar habitación" : "Agregar habitación"}</h1>}
                    footerButtonLeftText="Cancelar"
                    onClickLeftBtn={() => {
                        setMobileModalVisible(false);
                        resetEditor();
                    }}
                    footerButtonRightText={roomItem.room > 0 ? "Guardar" : "Agregar"}
                    onClickRightBtn={() => {
                        void executeSave().then((ok) => {
                            if (ok) setMobileModalVisible(false);
                        });
                    }}
                >
                    <div className="location-mobile-modal-content">
                        <div>
                            <label className="block mb-2">Nombre</label>
                            <InputTextCustom
                                className={(roomErrors.number ? "" : "border-grey") + " w-full"}
                                value={roomItem.number ?? ""}
                                onChange={(e) => {
                                    setRoomItem({ ...roomItem, number: e.target.value });
                                    setRoomErrors((prev) => ({ ...prev, number: "" }));
                                }}
                                error={!!roomErrors.number}
                                caption={roomErrors.number}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">Sector</label>
                            <Combobox
                                className={(roomErrors.sector ? "" : "border-grey") + " w-full"}
                                getItems={() => Promise.resolve(allowedSectors)}
                                optionLabel="name"
                                value={selectedSector}
                                setValue={(value: Sector | null) => {
                                    setSelectedSector(value);
                                    setRoomItem({
                                        ...roomItem,
                                        sector: value?.sector ?? -1,
                                        sectorname: value?.name ?? ""
                                    });
                                    setRoomErrors((prev) => ({ ...prev, sector: "" }));
                                }}
                                error={!!roomErrors.sector}
                                caption={roomErrors.sector}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">Tipo</label>
                        <Combobox
                            className="border-grey w-full"
                            getItems={getRoomTypeItems}
                            reLoadItemsValue={roomTypes}
                            optionLabel="description"
                            placeholder="Sin tipo"
                            autoselectOnOneItem={false}
                            value={selectedRoomType}
                            setValue={handleRoomTypeChange}
                            noItemsCaption="Creá los tipos desde «Tipos de habitación»"
                        />
                        </div>
                        <div className="flex align-items-center gap-2 location-switch-inline-row">
                            <InputSwitch
                                id="inputswitchroommobileid"
                                checked={roomItem.active}
                                onChange={(e) => setRoomItem({ ...roomItem, active: e.value })}
                            />
                            <span className="location-switch-state-label">{roomItem.active ? "Activo" : "Inactivo"}</span>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }

    return (
        <InputTable
            className="location-inputtable-fluid location-inputtable-fluid--room"
            columnGap={0}
            headerDivClassName="font-semibold bg-bluegray-50"
            dividerClassName="my-1 border-divider"
            buttonColumnWidth={43}
            items={rooms ?? []}
            columns={columns}
            onAdd={canCreateRooms ? (() => {}) : undefined}
            divBtnClassName="location-inputtable-hide-native-control"
            divider
            dividerInFirstItem
        />
    );
}
